import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Logo";
import { api } from "../../lib/api";
import { useLang } from "../../context/LangContext";
import { useStore } from "../../context/StoreContext";
import { NAV } from "./Header";
import { ERRORS } from "../../lib/i18n";

const Newsletter = () => {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/newsletter", { email });
      toast.success(t("subscribed"));
      setEmail("");
    } catch {
      toast.error(ERRORS.invalid_email[lang]);
    }
  };
  return (
    <section data-testid="newsletter-section" className="relative overflow-hidden bg-sand-light">
      <div className="container-q grid gap-10 py-20 sm:py-24 lg:grid-cols-2 lg:items-end">
        <div>
          <p className="eyebrow mb-4">Newsletter</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl">{t("newsletter_title")}</h2>
          <p className="mt-4 max-w-md text-sm leading-7 text-ink/70">{t("newsletter_desc")}</p>
        </div>
        <form onSubmit={submit} className="flex items-end gap-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("email")}
            data-testid="newsletter-email-input" className="field flex-1 border-ink/40" />
          <button data-testid="newsletter-submit-button" className="btn-ink">{t("subscribe")}</button>
        </form>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t, L, lang } = useLang();
  const { settings } = useStore();
  const c = settings?.contact || {};
  const year = new Date().getFullYear();
  const pages = [["p_shipping", "/page/shipping"], ["p_returns", "/page/returns"], ["p_privacy", "/page/privacy"], ["p_terms", "/page/terms"], ["faq", "/faq"]];
  return (
    <>
      {settings?.newsletter_enabled && <Newsletter />}
      <footer data-testid="site-footer" className="grain relative bg-ink text-ivory">
        <div className="container-q relative z-10 grid gap-14 py-20 md:grid-cols-12">
          <div className="md:col-span-5">
            <Logo light />
            <p className="mt-6 max-w-sm text-sm leading-7 text-ivory/60">{L(settings?.hero, "subtitle")}</p>
          </div>
          <div className="md:col-span-2">
            <p className="eyebrow mb-5 text-gold">{t("collection")}</p>
            {NAV.slice(1).map(([k, to]) => <Link key={k} to={to} className="block py-1.5 text-sm text-ivory/75 hover:text-gold">{t(k)}</Link>)}
          </div>
          <div className="md:col-span-2">
            <p className="eyebrow mb-5 text-gold">{t("p_terms")}</p>
            {pages.map(([k, to]) => <Link key={k} to={to} data-testid={`footer-link-${k}`} className="block py-1.5 text-sm text-ivory/75 hover:text-gold">{t(k)}</Link>)}
            <Link to="/track" className="block py-1.5 text-sm text-ivory/75 hover:text-gold">{t("track_order")}</Link>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow mb-5 text-gold">{t("contact")}</p>
            {c.email && <a href={`mailto:${c.email}`} className="flex items-center gap-3 py-1.5 text-sm text-ivory/75 hover:text-gold"><Mail size={15} strokeWidth={1.3} />{c.email}</a>}
            {c.phone && <a href={`tel:${c.phone}`} className="flex items-center gap-3 py-1.5 text-sm text-ivory/75 hover:text-gold" dir="ltr"><Phone size={15} strokeWidth={1.3} />{c.phone}</a>}
            {c.whatsapp && <a href={`https://wa.me/${c.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="block py-1.5 text-sm text-ivory/75 hover:text-gold">WhatsApp</a>}
            {c.instagram && <a href={c.instagram} target="_blank" rel="noreferrer" className="flex items-center gap-3 py-1.5 text-sm text-ivory/75 hover:text-gold"><Instagram size={15} strokeWidth={1.3} />Instagram</a>}
            {c.snapchat && <a href={c.snapchat} target="_blank" rel="noreferrer" className="block py-1.5 text-sm text-ivory/75 hover:text-gold">Snapchat</a>}
            {c.tiktok && <a href={c.tiktok} target="_blank" rel="noreferrer" className="block py-1.5 text-sm text-ivory/75 hover:text-gold">TikTok</a>}
            {c.x && <a href={c.x} target="_blank" rel="noreferrer" className="block py-1.5 text-sm text-ivory/75 hover:text-gold">X</a>}
          </div>
        </div>
        <div className="relative z-10 border-t border-ivory/10">
          <div className="container-q flex flex-col justify-between gap-3 py-6 text-xs text-ivory/50 sm:flex-row">
            <span data-testid="footer-copyright">© {year} QAMAR | قمر — {t("rights")}</span>
            <span className="flex gap-4"><span>{t("vat_included")}</span><Link to="/admin/login" data-testid="footer-admin-link" className="hover:text-gold">{lang === "ar" ? "دخول الإدارة" : "Admin"}</Link></span>
          </div>
        </div>
      </footer>
    </>
  );
};
